import { Link } from "react-router-dom";
import { ArrowRight, MapPin } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SITE, img, openEnquiry } from "../config";
import { CONNECTIVITY } from "../data/site";

const TITLE = "Atlanta Enclave Location | Shilphata, Thane";
const DESC =
    "Atlanta Enclave location – Shilphata, Thane. 2 mins from Shilphata Junction, 15 mins from Thane station, 43 mins from Navi Mumbai Airport. See connectivity & map.";

const JSON_LD = [
    {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: TITLE,
        url: "https://atlantaenclave.com/location/",
        description: DESC,
        isPartOf: { "@type": "WebSite", name: "Atlanta Enclave", url: "https://atlantaenclave.com/" },
    },
];

export default function Location() {
    return (
        <div data-testid="location-page">
            <Seo title={TITLE} description={DESC} path="/location/" jsonLd={JSON_LD} />
            <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Location" }]} />

            <section className="container-x mt-8">
                <Reveal>
                    <p className="eyebrow">Foothills of Thane</p>
                    <h1 className="font-display mt-3 max-w-3xl text-4xl font-medium leading-[1.1] text-[#23231f] sm:text-5xl">
                        Atlanta Enclave Location — Shilphata, Thane
                    </h1>
                    <p className="mt-5 max-w-2xl text-sm leading-relaxed text-[#55534a] sm:text-base">
                        Atlanta Enclave Shilphata is strategically located at the foothills of Thane, offering unmatched
                        connectivity and convenience. Surrounded by nature yet close to urban essentials, Shilphata connects you
                        seamlessly to Mumbai, Navi Mumbai, Thane and Pune — making property in Shilphata one of the
                        region&apos;s most promising addresses.
                    </p>
                </Reveal>

                <div className="mt-10 grid items-start gap-10 lg:grid-cols-2">
                    <Reveal>
                        <img
                            src={img("location-map")}
                            alt="Atlanta Enclave Shilphata location map"
                            className="frame-img w-full"
                            loading="lazy"
                            width="601"
                            height="579"
                            data-testid="location-page-map"
                        />
                        <p className="mt-4 flex items-start gap-2 text-sm text-[#55534a]">
                            <MapPin size={16} className="mt-0.5 shrink-0 text-[#8f7f45]" />
                            {SITE.address}
                        </p>
                    </Reveal>
                    <Reveal delay={120}>
                        <h2 className="font-display text-2xl font-semibold text-[#23231f]">Connectivity at a Glance</h2>
                        <ul className="mt-5 grid gap-0" data-testid="connectivity-list">
                            {CONNECTIVITY.map(([place, time]) => (
                                <li key={place} className="flex items-baseline justify-between gap-4 border-b border-[#ded8c6] py-2.5 text-sm">
                                    <span className="text-[#23231f]">{place}</span>
                                    <span className="shrink-0 font-bold text-[#6e6234]">{time}</span>
                                </li>
                            ))}
                        </ul>
                    </Reveal>
                </div>

                <Reveal className="mt-12">
                    <div className="card-line p-6 sm:p-8">
                        <h2 className="font-display text-2xl font-semibold text-[#23231f]">Flats in Shilphata — Close to Everything</h2>
                        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-[#55534a]">
                            Enjoy easy access to the city&apos;s best schools, hospitals, malls and entertainment hubs while
                            relishing the peace of a hillfront location. Explore the residences:{" "}
                            <Link to="/1-bhk/" className="font-semibold text-[#6e6234] underline underline-offset-2" data-testid="location-link-1bhk">1 BHK flats in Shilphata</Link>,{" "}
                            <Link to="/2-bhk/" className="font-semibold text-[#6e6234] underline underline-offset-2" data-testid="location-link-2bhk">2 BHK flats in Shilphata</Link> and{" "}
                            <Link to="/3-bhk/" className="font-semibold text-[#6e6234] underline underline-offset-2" data-testid="location-link-3bhk">3 BHK flats in Shilphata</Link>, or see the{" "}
                            <Link to="/amenities/" className="font-semibold text-[#6e6234] underline underline-offset-2" data-testid="location-link-amenities">amenities at Atlanta Enclave</Link>.
                        </p>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <button onClick={openEnquiry} className="btn btn-gold" data-testid="location-enquire-button">
                                Book Site Visit
                            </button>
                            <Link to="/contact/" className="btn btn-line" data-testid="location-contact-link">
                                Contact Us <ArrowRight size={15} />
                            </Link>
                        </div>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
